import { initializeApp } from 'firebase/app'
import { getAnalytics, isSupported as analyticsIsSupported } from 'firebase/analytics'
import {
  getMessaging,
  getToken,
  isSupported as messagingIsSupported,
  onMessage,
  type MessagePayload,
} from 'firebase/messaging'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

export const firebaseApp = initializeApp(firebaseConfig)

analyticsIsSupported().then((supported) => {
  if (supported) getAnalytics(firebaseApp)
}).catch(() => undefined)

export async function getAdminFcmToken() {
  if (!('Notification' in window) || typeof Notification.requestPermission !== 'function') {
    throw new Error('This browser does not support notification permission prompts.')
  }

  if (!window.isSecureContext) {
    throw new Error('Notifications need HTTPS or localhost to work.')
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.')
  }

  if (!(await messagingIsSupported())) {
    throw new Error('This browser does not support Firebase messaging.')
  }

  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported in this browser.')
  }
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim()
  if (!vapidKey) {
    throw new Error('Firebase web push key is missing. Please set VITE_FIREBASE_VAPID_KEY.')
  }

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
  const messaging = getMessaging(firebaseApp)

  return getToken(messaging, {
    serviceWorkerRegistration: registration,
    vapidKey,
  })
}

export async function listenForAdminPush(onPayload: (payload: MessagePayload) => void) {
  if (!(await messagingIsSupported())) return () => undefined
  const messaging = getMessaging(firebaseApp)
  return onMessage(messaging, onPayload)
}
